import React, { useEffect, useState } from 'react';
import { describeDecisionError } from '../../../utils/loadError';
import { Alert, Button, Form, Modal } from '@/components/ui';
import { ResourceRate } from '../../../services/vendorService';
import { shortDate } from '../utils/format';
import { rateChangeSummary } from '../utils/rateChange';
import BackdatedRateChoices from './BackdatedRateChoices';

export type RateDecision = 'approve' | 'reject' | 'withdraw';

interface Props {
  rate: ResourceRate | null;
  decision: RateDecision | null;
  onCancel: () => void;
  onConfirm: (rate: ResourceRate, decision: RateDecision, comments?: string) => Promise<void>;
}

// V2-053: the dialog title and its confirm button say what they do, in Title Case.
const TITLES: Record<RateDecision, string> = {
  approve: 'Approve Rate',
  reject: 'Reject Rate',
  withdraw: 'Withdraw Rate Request',
};

// V3-015: a failure reads "Couldn't approve the rate – try again." with Retry, in the dialog.
const VERBS: Record<RateDecision, string> = {
  approve: 'approve the rate',
  reject: 'reject the rate',
  withdraw: 'withdraw the request',
};

/**
 * Confirm step for Approve / Reject / Withdraw on the Vendor Approvals screen (M3, M5).
 *
 * M4 (Part 2): a backdated rate carries the Operations Coordinator's choice for each Hired
 * Equipment submission it affects. The approver sees those choices here; approving applies them
 * (submissions marked Update are repriced, the rest are left as they are), rejecting applies none.
 */
const RateDecisionModal: React.FC<Props> = ({ rate, decision, onCancel, onConfirm }) => {
  const [comments, setComments] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentMissing, setCommentMissing] = useState(false);

  useEffect(() => {
    setComments('');
    setError(null);
    setCommentMissing(false);
  }, [rate, decision]);

  if (!rate || !decision) return null;
  const summary = rateChangeSummary(rate);
  const choices = rate.backdatedChoices ?? [];
  const choiceByForm = Object.fromEntries(choices.map((c) => [c.formId, c.choice]));
  const startsInPast = rate.effectiveFrom.slice(0, 10) < new Date().toISOString().slice(0, 10);

  const confirm = async () => {
    // V2-056: Reject needs a comment; say so under the field instead of sending the request.
    if (decision === 'reject' && !comments.trim()) {
      setCommentMissing(true);
      // V3-020: focus moves to the field with the error, scrolled into view.
      setTimeout(() => {
        const field = document.getElementById('rate-reject-comment');
        field?.focus();
        field?.scrollIntoView?.({ block: 'center' });
      }, 0);
      return;
    }
    try {
      setBusy(true);
      setError(null);
      await onConfirm(rate, decision, comments.trim() || undefined);
    } catch (err) {
      setError(describeDecisionError(err, VERBS[decision]));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show onHide={busy ? () => undefined : onCancel} size="lg" autoHeight>
      <Modal.Header closeButton>
        <Modal.Title>{TITLES[decision]}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" role="alert" className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <span>{error}</span>
            <Button type="button" size="sm" variant="outline-secondary" className="tw-ml-auto" onClick={confirm} disabled={busy}>
              Retry
            </Button>
          </Alert>
        )}
        <p className="tw-mb-3">
          <strong>{rate.resource?.name ?? `Resource ${rate.resourceId}`}</strong>
          {rate.resource?.vendor?.name ? ` (${rate.resource.vendor.name})` : ''} in{' '}
          {rate.serviceArea?.name ?? 'this service area'}: {summary.text}, effective{' '}
          {shortDate(rate.effectiveFrom)}
          {rate.effectiveTo ? ` to ${shortDate(rate.effectiveTo)}` : ''}.
        </p>

        {decision === 'approve' && (
          <>
            {startsInPast && choices.length > 0 && (
              <Alert variant="warning" data-testid="backdated-rate-choices">
                <p className="tw-mb-2">
                  <strong>This rate starts in the past</strong> ({shortDate(rate.effectiveFrom)}). The coordinator chose
                  for each Hired Equipment submission it affects. Approving applies these choices: Update reprices the
                  submission at the new rate (an Approved one stays Approved); Leave as is changes nothing.
                </p>
                <BackdatedRateChoices submissions={choices} choices={choiceByForm} />
              </Alert>
            )}
            {startsInPast && choices.length === 0 && (
              <Alert variant="info">
                This rate starts in the past ({shortDate(rate.effectiveFrom)}). No Hired Equipment submission was
                affected when it was entered, so approving it reprices nothing.
              </Alert>
            )}
            <p className="tw-mb-0">
              Once approved, this rate is used for pricing from {shortDate(rate.effectiveFrom)}
              {summary.priorRate !== null ? ' and the current rate ends the day before.' : '.'}
            </p>
          </>
        )}

        {decision === 'reject' && (
          <>
            {choices.length > 0 && (
              <Alert variant="warning" data-testid="reject-affected-submissions">
                <p className="tw-mb-2">
                  Rejecting means the coordinator&apos;s choices for these Hired Equipment submissions will not
                  be applied. They stay at the rate they were charged.
                </p>
                <BackdatedRateChoices submissions={choices} choices={choiceByForm} />
              </Alert>
            )}
            <Form.Group controlId="rate-reject-comment">
              <Form.Label>
                Comment <span className="tw-text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={comments}
                isInvalid={commentMissing}
                aria-describedby={commentMissing ? 'rate-reject-comment-error' : undefined}
                onChange={(e) => {
                  setComments(e.target.value);
                  if (e.target.value.trim()) setCommentMissing(false);
                }}
                placeholder="Tell the requester why"
              />
              {commentMissing && (
                <Form.Control.Feedback id="rate-reject-comment-error" role="alert">
                  Comment required
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </>
        )}

        {decision === 'withdraw' && (
          <p className="tw-mb-0">The request is removed and cannot be recovered. You can submit a new one later.</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant={decision === 'approve' ? 'primary' : 'danger'}
          onClick={confirm}
          disabled={busy}
        >
          {busy ? 'Working…' : TITLES[decision]}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RateDecisionModal;
